import { ContextualInfoButton } from "@/components/buttons/ContextualInfoButton";
import ContextualInfoCollapse from "@/components/buttons/ContextualInfoCollapse";
import { getExposureStatus, useCollectionTutorialsState } from "@/features/collection_home";
import type { AnalysisResultsTab, HubMapDisplayPoint } from "@/features/maps";
import { availableExposureHazards, HazardPlusTotal, isExposureHazard } from "@/features/pe_aldd";
import { ensureGradedRiskValue, RiskBand } from "@/features/risk_bands";
import { adaptationTermsConfig } from "@/types/adaptation";
import type { ExposureStatus } from "@/types/exposure";
import type { ReturnPeriod } from "@/types/returnPeriod";
import { getAnalysisTypeName } from "@/utils/string";
import { EyeOutlined, FilterOutlined, SearchOutlined } from "@ant-design/icons";
import { ArrowRightIcon } from "@phosphor-icons/react";
import { CaretDownIcon, CaretRightIcon, CaretUpIcon, WarningIcon } from "@phosphor-icons/react/dist/ssr";
import { useVirtualizer, VirtualItem } from "@tanstack/react-virtual";
import { Button, Input, Select, Tooltip, Typography } from "antd";
import classNames from "classnames";
import { type ReactNode, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { MvarDerivedUnavailableNotice } from "../../collection_home/components/charts/common/MvarDerivedUnavailableNotice";
import { isMvarDerivedDataAvailable } from "../../collection_home/components/charts/common/utils";
import CompareModeSelector from "../../collection_home/components/CompareModeSelector";
import type { CompareResultsMode } from "../../collection_home/types";
import type { CompareSegment } from "../../collection_home/utils/compareModeState";
import { useDataColors } from "../useDataColors";
import ExposureItemIcon from "./ExposureItemIcon";
const {
  Title
} = Typography;
const OVERSCAN = 6; // num of extra items to render below/above visible area
const ROW_HEIGHT = 38;

/** Shared by the exposure header cell and the exposure row cell so the two cannot drift. */
const EXPOSURE_COLUMN_WIDTH = 96;
const riskColumnsTemplate = (resultsMode: CompareResultsMode): string => resultsMode === "compare" ? `${100}px ${24}px ${168}px` : `${resultsMode === "baseline" ? 100 : 168}px`;
interface PointsSidebarProps {
  activeTab: AnalysisResultsTab;
  activeHazard: HazardPlusTotal;
  displayReturnPeriod?: ReturnPeriod;
  points: HubMapDisplayPoint[];
  displayYear?: number;
  highlightedId: string | null;
  onSelect: (pointId: string) => void;
  onHighlight: (pointId: string | null) => void;
  onDimmedPointsChange?: (dimmedPointIds: string[]) => void;
  searchQuery: string;
  setSearchQuery: (value: string) => void;
  sortOption: SortOption;
  setSortOption: (value: SortOption) => void;
  sortOrder: SortOrder;
  setSortOrder: (value: SortOrder) => void;
  filterOption: FilterOption;
  setFilterOption: (value: FilterOption) => void;
  exposureFilterOption: ExposureFilterOption;
  setExposureFilterOption: (value: ExposureFilterOption) => void;
  riskiestIds?: string[];
  scrollElement: HTMLElement | null;
  canRunAutoAdaptation: boolean;
  adaptedRatingByName?: Map<string, string | null>;
  resultsMode?: CompareResultsMode;
  setResultsMode?: (mode: CompareResultsMode) => void;
  compareSegments?: CompareSegment[];
  updateAvailable: boolean;
  isLargeSite: boolean;
}
export type SortOption = "alphabetical" | "rating" | "adaptedRating" | "exposure";
export type SortOrder = "asc" | "desc";
export type FilterOption = "all" | "high" | "moderate" | "low";
export type ExposureFilterOption = "all" | "?" | ExposureStatus;
interface SidebarItem {
  id: string;
  label: string;
  rating: React.ReactNode;
  point: HubMapDisplayPoint;
  grade: string;
  adaptedGrade: string;
}
const extractRating = (response: HubMapDisplayPoint["response"], displayYear?: number): string | null | undefined => {
  if (response && "ratings" in response) {
    const ratings = response.ratings || [];
    return displayYear ? ratings.find(r => r.year === displayYear)?.rating ?? ratings[0]?.rating : ratings[0]?.rating;
  } else if (response && "end_of_mortgage_term" in response) {
    return response.end_of_mortgage_term?.rating;
  }
  return undefined;
};

// Temporary component
const RatedLabel = ({
  grade,
  color
}: {
  grade: string;
  color: string;
}) => <span className="inline-flex h-6 w-6 items-center justify-center rounded-full text-sm font-bold text-gray-700" style={{
  border: `2px solid ${color}`
}} data-sentry-component="RatedLabel" data-sentry-source-file="SidebarPointList.tsx">
    {grade}
  </span>;
const filterByRating = (grade: string, filter: FilterOption): boolean => {
  if (filter === "all") return true;
  if (filter === "low") return grade === "A";
  if (filter === "moderate") return grade === "B";
  if (filter === "high") return grade === "C" || grade === "D" || grade === "E";
  return true;
};

/** Severity of a risk grade, ascending. Ungraded ("?") has no rank and sorts last either way. */
const RISK_SEVERITY: Record<string, number> = {
  A: 0,
  B: 1,
  C: 2,
  D: 3,
  E: 4
};

/** Direction each column starts in when first selected. */
export const DEFAULT_SORT_ORDER: Record<SortOption, SortOrder> = {
  alphabetical: "asc",
  rating: "desc",
  adaptedRating: "desc",
  exposure: "desc"
};
const activeGrade = (item: SidebarItem, resultsMode: CompareResultsMode): string => resultsMode === "enhanced_resilience" ? item.adaptedGrade : item.grade;

/** Weights an exposed hazard above any number of merely unexposed ones, so "most exposed" wins ties. */
const EXPOSED_WEIGHT = 100;
const exposureResult = (item: SidebarItem, returnPeriod?: ReturnPeriod, activeHazard?: HazardPlusTotal): number | null => {
  const exposures = item.point.response.exposures;
  if (!exposures || returnPeriod === undefined || activeHazard === undefined) return null;
  if (activeHazard === "total") {
    let exposed = 0;
    let unexposed = 0;
    for (const hazard of availableExposureHazards) {
      const status = exposures[hazard][returnPeriod];
      if (status === "exposed") exposed++;
      if (status === "unexposed") unexposed++;
    }
    // every hazard lacking data means there is nothing to rank
    if (exposed === 0 && unexposed === 0) return null;
    return exposed * EXPOSED_WEIGHT + unexposed;
  }
  if (!isExposureHazard(activeHazard)) return null;
  const status = exposures[activeHazard][returnPeriod];
  if (status === "exposed") return EXPOSED_WEIGHT;
  if (status === "unexposed") return 1;
  return null;
};
const sortItems = (items: SidebarItem[], sortOption: SortOption, sortOrder: SortOrder, returnPeriod?: ReturnPeriod, activeHazard?: HazardPlusTotal, riskiestIds?: string[], resultsMode: CompareResultsMode = "baseline"): SidebarItem[] => {
  // null = no data, which sorts last in BOTH directions, so those branches
  // must sit outside the direction flip below.
  const valueOf = (item: SidebarItem): number | string | null => {
    switch (sortOption) {
      case "alphabetical":
        return item.label;
      case "rating":
        return RISK_SEVERITY[activeGrade(item, resultsMode)] ?? null;
      case "adaptedRating":
        return RISK_SEVERITY[item.adaptedGrade] ?? null;
      case "exposure":
        return exposureResult(item, returnPeriod, activeHazard);
    }
  };
  const direction = sortOrder === "asc" ? 1 : -1;
  const sorted = [...items].sort((a, b) => {
    const valueA = valueOf(a);
    const valueB = valueOf(b);
    if (valueA === null && valueB === null) return 0;
    if (valueA === null) return 1;
    if (valueB === null) return -1;
    if (typeof valueA === "string" && typeof valueB === "string") return valueA.localeCompare(valueB, undefined, {
      numeric: true,
      sensitivity: "base"
    }) * direction;
    return ((valueA as number) - (valueB as number)) * direction;
  });

  // Pinning only holds while the list already reads worst-first; any other order contradicts it.
  const isRiskSort = sortOption === "rating" || sortOption === "adaptedRating";
  if (isRiskSort && sortOrder === "desc" && riskiestIds?.length) {
    const risky = sorted.filter(item => riskiestIds.includes(item.id));
    const others = sorted.filter(item => !riskiestIds.includes(item.id));
    return [...risky, ...others];
  }
  return sorted;
};
export const SidebarPointList = ({
  activeHazard,
  displayReturnPeriod,
  activeTab,
  points,
  displayYear,
  highlightedId,
  onSelect,
  onHighlight,
  onDimmedPointsChange,
  searchQuery,
  setSearchQuery,
  sortOption: storedSortOption,
  setSortOption,
  sortOrder: storedSortOrder,
  setSortOrder,
  filterOption,
  setFilterOption,
  exposureFilterOption,
  setExposureFilterOption,
  riskiestIds,
  scrollElement,
  canRunAutoAdaptation,
  adaptedRatingByName,
  resultsMode = "baseline",
  setResultsMode = () => null,
  compareSegments = [],
  updateAvailable,
  isLargeSite
}: PointsSidebarProps) => {
  const {
    t
  } = useTranslation("tools", {
    keyPrefix: "portfolios.pointLevel.tabs"
  });
  const {
    t: tAssetRisk
  } = useTranslation("tools", {
    keyPrefix: "portfolios.pointLevel.assetRisk"
  });
  const {
    ratings
  } = useDataColors();

  // Ungraded while unavailable, so the rating filter and sort cannot act on hidden grades.
  const ratingsAvailable = isMvarDerivedDataAvailable(activeHazard);
  const ratingFilter = ratingsAvailable ? filterOption : "all";

  // Compare mode drops out while adaptation data reloads. Fall back for the reload rather than
  // discarding the choice; PointLevel retires the sort when the user actually leaves compare.
  const adaptedSortUnavailable = storedSortOption === "adaptedRating" && resultsMode !== "compare";
  const sortOption = adaptedSortUnavailable ? "rating" : storedSortOption;
  const sortOrder = adaptedSortUnavailable ? DEFAULT_SORT_ORDER.rating : storedSortOrder;
  const allItems = useMemo<SidebarItem[]>(() => {
    return points.map(point => {
      const rating = extractRating(point.response, displayYear);
      const graded = ratingsAvailable && rating ? ensureGradedRiskValue(rating as any) : "?";
      const color = ratings[graded];
      const adaptedRating = adaptedRatingByName?.get(point.name);
      const adaptedGrade = ratingsAvailable && adaptedRating ? ensureGradedRiskValue(adaptedRating as any) : "?";
      return {
        id: point.name,
        label: point.displayInput || point.name,
        rating: <RatedLabel grade={graded} color={color} />,
        point,
        grade: graded,
        adaptedGrade
      };
    });
  }, [points, displayYear, adaptedRatingByName, ratingsAvailable]);
  const filteredAndSortedItems = useMemo(() => {
    const searchFiltered = allItems.filter(item => {
      const searchLower = searchQuery.toLowerCase();
      return String(item.label || "").toLowerCase().includes(searchLower);
    });
    const ratingFiltered = searchFiltered.filter(item => filterByRating(activeGrade(item, resultsMode), ratingFilter));
    const exposureFiltered = searchFiltered.filter(item => {
      if (exposureFilterOption === "all") return true;
      const exposure = item.point.response.exposures;
      if (exposure === undefined || exposure === null) return false;
      if (activeHazard === "total") {
        return getExposureStatus(item.point.response, displayReturnPeriod) === exposureFilterOption;
      }
      if (!isExposureHazard(activeHazard)) return false;
      return displayReturnPeriod ? (exposure[activeHazard][displayReturnPeriod] ?? "?") === exposureFilterOption : true;
    });
    return sortItems(activeTab === "exposure" ? exposureFiltered : ratingFiltered, sortOption, sortOrder, displayReturnPeriod, activeHazard, riskiestIds, resultsMode);
  }, [allItems, searchQuery, ratingFilter, exposureFilterOption, sortOption, sortOrder, displayReturnPeriod, activeHazard, riskiestIds, resultsMode]);
  const dimmedPointIds = useMemo(() => {
    const visibleIds = new Set(filteredAndSortedItems.map(item => item.id));
    return allItems.filter(item => !visibleIds.has(item.id)).map(item => item.id);
  }, [filteredAndSortedItems, allItems]);
  useEffect(() => {
    onDimmedPointsChange?.(dimmedPointIds);
  }, [dimmedPointIds, onDimmedPointsChange]);
  const {
    selectFirstItemInAssetList,
    isTutorialOpen
  } = useCollectionTutorialsState();
  useEffect(() => {
    if (isTutorialOpen && selectFirstItemInAssetList && filteredAndSortedItems.length > 0) {
      onSelect(filteredAndSortedItems[0].id);
    }
  }, [selectFirstItemInAssetList]);
  const handleSort = (key: SortOption) => {
    if (key === sortOption) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
      setSortOption(key);
      return;
    }
    setSortOption(key);
    setSortOrder(DEFAULT_SORT_ORDER[key]);
  };
  return <div className="animate-fade-in-slide flex min-h-full flex-col @container/panel" data-sentry-component="SidebarPointList" data-sentry-source-file="SidebarPointList.tsx">
      <div className="!mb-2 block justify-between gap-2 px-2 pt-4 @xl/panel:flex @xl/panel:items-end">
        <Title className="!mb-0 min-w-0 flex-1 truncate" level={4} data-sentry-element="Title" data-sentry-source-file="SidebarPointList.tsx">
          All {getAnalysisTypeName(isLargeSite, true)}
        </Title>

        {!isLargeSite && updateAvailable && activeTab === "risk" && <div className="shrink-0">
            <CompareModeSelector value={resultsMode} size="small" onChange={setResultsMode} options={compareSegments} />
          </div>}
      </div>
      <ContextualInfoCollapse className="mx-2 mb-2" data-sentry-element="ContextualInfoCollapse" data-sentry-source-file="SidebarPointList.tsx">{t(`${activeTab}.note`)}</ContextualInfoCollapse>

      <Filters activeTab={activeTab} searchQuery={searchQuery} setSearchQuery={setSearchQuery} filterOption={ratingFilter} setFilterOption={setFilterOption} ratingFilterDisabled={!ratingsAvailable} exposureFilterOption={exposureFilterOption} setExposureFilterOption={setExposureFilterOption} data-sentry-element="Filters" data-sentry-source-file="SidebarPointList.tsx" />
      {activeTab === "risk" && !ratingsAvailable && <MvarDerivedUnavailableNotice message={tAssetRisk("charts.mvar.unavailableMessage")} className="border-b border-red-100" />}
      <PointList activeTab={activeTab} activeHazard={activeHazard} displayReturnPeriod={displayReturnPeriod} filteredAndSortedItems={filteredAndSortedItems} highlightedId={highlightedId} onSelect={onSelect} onHighlight={onHighlight} riskiestIds={riskiestIds} scrollElement={scrollElement} resultsMode={resultsMode} canRunAutoAdaptation={canRunAutoAdaptation} ratingsAvailable={ratingsAvailable} sortOption={sortOption} sortOrder={sortOrder} onSort={handleSort} data-sentry-element="PointList" data-sentry-source-file="SidebarPointList.tsx" />

      <div className="sticky bottom-0 z-10 mt-auto flex-shrink-0 border-t border-gray-100 bg-gray-50 px-2 py-2">
        <div className="text-xs text-gray-600">
          {filteredAndSortedItems.length === allItems.length ? <span>
              {allItems.length} {allItems.length === 1 ? "point" : "points"}
            </span> : <span>
              {filteredAndSortedItems.length} of {allItems.length} {allItems.length === 1 ? "point" : "points"}
            </span>}
        </div>
      </div>
    </div>;
};
const SelectPrefixIcon = ({
  children
}: {
  children: ReactNode;
}) => <span className="mr-1.5 inline-flex shrink-0 items-center border-r border-gray-300 pr-1.5 text-gray-400" data-sentry-component="SelectPrefixIcon" data-sentry-source-file="SidebarPointList.tsx">
    {children}
  </span>;
const Filters = ({
  activeTab,
  searchQuery,
  setSearchQuery,
  filterOption,
  setFilterOption,
  ratingFilterDisabled,
  exposureFilterOption,
  setExposureFilterOption
}: {
  activeTab: AnalysisResultsTab;
  searchQuery: string;
  setSearchQuery: (value: string) => void;
  filterOption: FilterOption;
  setFilterOption: (value: FilterOption) => void;
  ratingFilterDisabled: boolean;
  exposureFilterOption: ExposureFilterOption;
  setExposureFilterOption: (value: ExposureFilterOption) => void;
}) => {
  const {
    riskAssetSearch
  } = useCollectionTutorialsState();
  return <div ref={riskAssetSearch} className="flex-shrink-0 border-y border-gray-200 bg-gray-50 px-2 py-2" data-sentry-component="Filters" data-sentry-source-file="SidebarPointList.tsx">
      <div className="flex items-center gap-1.5">
        <Input placeholder="Search points..." prefix={<SearchOutlined className="text-gray-400" />} value={searchQuery} onChange={e => setSearchQuery(e.target.value)} allowClear className="flex-1" size="middle" data-sentry-element="Input" data-sentry-source-file="SidebarPointList.tsx" />

        {activeTab === "exposure" ? <Select size="middle" value={exposureFilterOption} onChange={value => setExposureFilterOption(value as ExposureFilterOption)} className="!w-40" placeholder="Filter" prefix={<SelectPrefixIcon>
                <FilterOutlined />
              </SelectPrefixIcon>} options={[{
        value: "all",
        label: "All"
      }, {
        value: "exposed",
        label: "Exposed"
      }, {
        value: "unexposed",
        label: "Not Exposed"
      }, {
        value: "?",
        label: "No Data"
      }]} /> : <Select size="middle" value={filterOption} onChange={value => setFilterOption(value as FilterOption)} disabled={ratingFilterDisabled} className="!w-40" placeholder="Filter" prefix={<SelectPrefixIcon>
                <FilterOutlined />
              </SelectPrefixIcon>} options={[{
        value: "all",
        label: "All"
      }, {
        value: "high",
        label: "High Risk"
      }, {
        value: "moderate",
        label: "Moderate"
      }, {
        value: "low",
        label: "Low Risk"
      }]} />}
      </div>
    </div>;
};
const ViewSlideButton = () => <div className="flex w-[56px] shrink-0 items-center justify-end rounded-full py-0.5 pl-2 pr-1 text-gray-500 transition-colors duration-200 group-hover:bg-gray-200 group-hover:text-gray-500" data-sentry-component="ViewSlideButton" data-sentry-source-file="SidebarPointList.tsx">
    <span aria-hidden className="max-w-0 translate-x-1 overflow-hidden whitespace-nowrap text-xs opacity-0 transition-all duration-200 group-hover:mr-1 group-hover:max-w-[40px] group-hover:translate-x-0 group-hover:opacity-100">
      View
    </span>
    <CaretRightIcon size={12} weight="bold" className="shrink-0" data-sentry-element="CaretRightIcon" data-sentry-source-file="SidebarPointList.tsx" />
  </div>;
const SortCarets = ({
  order
}: {
  order?: SortOrder;
}) => <span aria-hidden className="inline-flex shrink-0 flex-col -space-y-1" data-sentry-component="SortCarets" data-sentry-source-file="SidebarPointList.tsx">
    <CaretUpIcon size={9} weight="fill" className={order === "asc" ? "text-gray-800" : "text-gray-400"} data-sentry-element="CaretUpIcon" data-sentry-source-file="SidebarPointList.tsx" />
    <CaretDownIcon size={9} weight="fill" className={order === "desc" ? "text-gray-800" : "text-gray-400"} data-sentry-element="CaretDownIcon" data-sentry-source-file="SidebarPointList.tsx" />
  </span>;
const SortableColumnHeader = ({
  label,
  sortKey,
  sortOption,
  sortOrder,
  onSort,
  contextId,
  width,
  align = "left"
}: {
  label: string;
  sortKey: SortOption;
  sortOption: SortOption;
  sortOrder: SortOrder;
  onSort: (key: SortOption) => void;
  contextId?: string;
  width?: number;
  align?: "left" | "center";
}) => {
  const isActive = sortKey === sortOption;
  return <Tooltip title="Click to sort" data-sentry-element="Tooltip" data-sentry-component="SortableColumnHeader" data-sentry-source-file="SidebarPointList.tsx">
      <div role="button" tabIndex={0} aria-label={`Sort by ${label}`} onClick={() => onSort(sortKey)} onKeyDown={e => {
      // the info button nested below is a real <button>; leave its own keys alone
      if (e.target !== e.currentTarget) return;
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onSort(sortKey);
      }
    }} style={{
      width
    }} className={classNames(
    // no horizontal padding: it would inset the label past the row content below it
    "flex min-w-0 flex-1 cursor-pointer select-none items-center gap-x-2 gap-y-1 rounded py-2 text-xs font-semibold hover:bg-gray-100 hover:text-gray-700", align === "center" && "justify-center", isActive ? "text-gray-700" : "text-gray-500")}>
        {/* wraps rather than overflowing, so the carets stay inside the column */}
        <span className="min-w-0 leading-tight">{label}</span>
        {contextId && <ContextualInfoButton id={contextId} className="shrink-0 !text-xs" />}
        <SortCarets order={isActive ? sortOrder : undefined} data-sentry-element="SortCarets" data-sentry-source-file="SidebarPointList.tsx" />
      </div>
    </Tooltip>;
};

/** Column labels, sharing the row widths so the list reads as a table. */
const PointListHeader = ({
  activeTab,
  resultsMode,
  canRunAutoAdaptation,
  sortOption,
  sortOrder,
  onSort
}: {
  activeTab: AnalysisResultsTab;
  resultsMode: CompareResultsMode;
  canRunAutoAdaptation: boolean;
  sortOption: SortOption;
  sortOrder: SortOrder;
  onSort: (key: SortOption) => void;
}) => {
  const {
    t: tTerms
  } = useTranslation("terms", {
    keyPrefix: "adaptationTypes"
  });
  const sortProps = {
    sortOption,
    sortOrder,
    onSort
  };
  return <div className="sticky top-0 z-10 flex items-center border-b border-gray-200 bg-gray-50 !p-1.5 !py-0 text-gray-500" data-sentry-component="PointListHeader" data-sentry-source-file="SidebarPointList.tsx">
      <div className="flex min-w-0 flex-1 items-center gap-2">
        <div className="w-6 shrink-0" />
        <SortableColumnHeader label="Name" sortKey="alphabetical" {...sortProps} data-sentry-element="SortableColumnHeader" data-sentry-source-file="SidebarPointList.tsx" />
      </div>
      <div className="flex shrink-0 items-center gap-1">
        {activeTab === "exposure" ? <div className="flex" style={{
        width: EXPOSURE_COLUMN_WIDTH
      }}>
            <SortableColumnHeader label="Exposure" sortKey="exposure" align="center" {...sortProps} />
          </div> : <div className="grid items-center" style={{
        gridTemplateColumns: riskColumnsTemplate(resultsMode)
      }}>
            {resultsMode === "compare" ? <>
                <SortableColumnHeader label={tTerms("baseline")} sortKey="rating" contextId={adaptationTermsConfig.baseline.contextId} align="center" {...sortProps} />
                <div className="flex items-center justify-center">
                  <ArrowRightIcon size={12} aria-hidden className="text-gray-400" />
                </div>
                <SortableColumnHeader label={tTerms("enhanced_resilience")} sortKey="adaptedRating" contextId={adaptationTermsConfig.enhanced_resilience.contextId} align="center" {...sortProps} />
              </> : <SortableColumnHeader label={canRunAutoAdaptation ? tTerms(resultsMode) : "Risk"} sortKey="rating" contextId={canRunAutoAdaptation ? adaptationTermsConfig[resultsMode].contextId : undefined} align="center" {...sortProps} />}
          </div>}
        <div className="w-[56px] shrink-0" />
      </div>
    </div>;
};
const PointList = ({
  activeTab,
  activeHazard,
  displayReturnPeriod,
  filteredAndSortedItems,
  highlightedId,
  onSelect,
  onHighlight,
  riskiestIds,
  scrollElement,
  resultsMode,
  canRunAutoAdaptation,
  ratingsAvailable,
  sortOption,
  sortOrder,
  onSort
}: {
  activeTab: AnalysisResultsTab;
  activeHazard: HazardPlusTotal;
  displayReturnPeriod?: ReturnPeriod;
  filteredAndSortedItems: SidebarItem[];
  highlightedId: string | null;
  onSelect: (id: string) => void;
  onHighlight: (id: string | null) => void;
  riskiestIds?: string[];
  scrollElement: HTMLElement | null;
  resultsMode: CompareResultsMode;
  canRunAutoAdaptation: boolean;
  ratingsAvailable: boolean;
  sortOption: SortOption;
  sortOrder: SortOrder;
  onSort: (key: SortOption) => void;
}) => {
  const listRef = useRef<HTMLDivElement>(null);
  const {
    riskItemSelect
  } = useCollectionTutorialsState();
  const SCROLL_MARGIN = 50; // hard coded bc height of filters is uniform

  const virtualizer = useVirtualizer({
    count: filteredAndSortedItems.length,
    getScrollElement: () => scrollElement,
    estimateSize: () => ROW_HEIGHT,
    overscan: OVERSCAN,
    scrollMargin: SCROLL_MARGIN
  });

  // reset to top when the list contents change (sort / filter / search)
  useEffect(() => {
    if (scrollElement) scrollElement.scrollTop = 0;
  }, [filteredAndSortedItems, scrollElement]);
  return <>
      <PointListHeader activeTab={activeTab} resultsMode={resultsMode} canRunAutoAdaptation={canRunAutoAdaptation} sortOption={sortOption} sortOrder={sortOrder} onSort={onSort} data-sentry-element="PointListHeader" data-sentry-source-file="SidebarPointList.tsx" />
      <div className="gap-2 pb-4 pt-2">
        {filteredAndSortedItems.length === 0 ? <div className="p-4 text-center text-sm text-gray-500">No points match your filters</div> : <div ref={listRef} className="relative w-full" style={{
        height: virtualizer.getTotalSize()
      }}>
            {virtualizer.getVirtualItems().map((virtualRow: VirtualItem) => {
          const {
            id,
            label,
            grade,
            adaptedGrade,
            point
          } = filteredAndSortedItems[virtualRow.index];
          const isHighlighted = id === highlightedId;
          const exposures = point.response.exposures;
          return <div key={id}
          // the tour points at the top row to demonstrate opening an asset
          ref={virtualRow.index === 0 ? riskItemSelect : undefined} role="button" tabIndex={0} className={`group absolute inset-x-0 top-0 box-border flex cursor-pointer items-center border-b border-gray-100 !p-1.5 pl-3 transition-colors duration-200 hover:bg-gray-100 hover:font-semibold ${isHighlighted ? "border-gray-200 bg-gray-100 font-semibold" : ""}`} style={{
            height: ROW_HEIGHT,
            transform: `translateY(${virtualRow.start - SCROLL_MARGIN}px)`
          }} onClick={() => onSelect(id)} onKeyDown={e => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onSelect(id);
            }
          }}>
                  <div className="flex min-w-0 flex-1 items-center gap-2">
                    <Button type={isHighlighted ? "primary" : "text"} size="small" icon={<EyeOutlined />} onClick={e => {
                e.stopPropagation();
                e.preventDefault();
                onHighlight(id);
              }} title="Highlight on map" className={classNames("shrink-0 text-gray-400", isHighlighted ? "text-white" : "")} />
                    <div className="flex min-w-0 items-center gap-1.5">
                      <div className="min-w-0 truncate text-sm">{label || "Unnamed asset"}</div>
                      {riskiestIds?.includes(id) && <Tooltip title="Riskiest point">
                          <WarningIcon size={16} className="shrink-0 text-amber-600" weight="fill" />
                        </Tooltip>}
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    {activeTab === "exposure" ? <div className="flex items-center justify-center gap-1" style={{
                width: EXPOSURE_COLUMN_WIDTH
              }}>
                        {exposures && displayReturnPeriod && <ExposureItemIcon activeHazard={activeHazard} exposures={exposures} returnPeriod={displayReturnPeriod} />}
                      </div> : <div className="grid items-center justify-items-center" style={{
                gridTemplateColumns: riskColumnsTemplate(resultsMode)
              }}>
                        {resultsMode === "compare" ? <>
                            <RiskBand rating={grade} size={24} notApplicable={!ratingsAvailable} />
                            <div className="flex items-center justify-center text-center">
                              <ArrowRightIcon size={12} aria-hidden className="text-gray-400" />
                            </div>
                            <RiskBand rating={adaptedGrade} size={24} notApplicable={!ratingsAvailable} />
                          </> : <RiskBand rating={resultsMode === "enhanced_resilience" ? adaptedGrade : grade} size={24} notApplicable={!ratingsAvailable} />}
                      </div>}
                    <ViewSlideButton />
                  </div>
                </div>;
        })}
          </div>}
      </div>
    </>;
};